import { Stack } from "@mui/material";
import { AnonymousPaymentDetail } from "Components/AnonymousCustomer/Payment/PaymentDetail/AnonymousPaymentDetail";
import { BankSelector } from "Components/AnonymousCustomer/Payment/BankConnection/BankSelector";
import { BankAccountSelector } from "Components/AnonymousCustomer/Payment/AccountSelection/BankAccountSelector";
import { AnonymousPaymentStepper } from "Components/AnonymousCustomer/Payment/Stepper/AnonymousPaymentStepper";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useEffect, useState } from "react";
import type { PaymentStep } from "Components/AnonymousCustomer/Payment/config";
import { SignInPrompt } from "Components/AnonymousCustomer/Payment/PaymentDetail/SignInPrompt";
import { useSelector } from "react-redux";
import type { RootStateType } from "State/Store";
import { appUrl } from "Utils/UrlUtils";

type Props = {
  transactionPublicID: string;
  activeStep: PaymentStep;
  onActiveStepChange: (step: PaymentStep) => void;
  campaignCode?: string;
  affiliateCode?: string;
};

export const AnonymousPaymentPageContent = ({
  transactionPublicID,
  activeStep,
  onActiveStepChange,
  campaignCode,
  affiliateCode,
}: Props) => {
  const [erroringStep, setErroringStep] = useState<PaymentStep | null>(null);

  // ----------------------------------------------------------------------------------
  // Note:
  // When the user makes a isPasswordlessSign (first anonymous payment), the navigateToStep in onNextStepRequested of AnonymousPaymentDetail is called but the active step state doesn't update.
  // It is most likely lost somewhere in the rerendering of this component and the state isn't changed.
  // It's probably some race condition between redux state and react state updates, as I couldn't reproduce it locally, only in deployed versions of the app.
  //
  // This is a workaround fix to ensure that the step is changed to bank after the sign up is successful.
  // Ideally, it should be refactored so that the effect isn't necessary, and the react state updates correctly.
  const isPasswordlessSign = useSelector(
    (state: RootStateType) => state.auth.isPasswordlessSign,
  );
  useEffect(() => {
    if (isPasswordlessSign) {
      onActiveStepChange("bank");
    }
  }, [isPasswordlessSign, onActiveStepChange]);
  // -----------------------------------------------------------------------------------

  const navigateToStep = (step: PaymentStep) => {
    onActiveStepChange(step);
    setErroringStep(null);
  };

  const stepContentLookup = {
    detail: (
      <AnonymousPaymentDetail
        transactionPublicID={transactionPublicID}
        onError={() => setErroringStep("detail")}
        onNextStepRequested={(nextStep) => navigateToStep(nextStep ?? "bank")}
        campaignCode={campaignCode}
        affiliateCode={affiliateCode}
      />
    ),
    bank: (
      <BankSelector
        transactionPublicID={transactionPublicID}
        onError={() => setErroringStep("bank")}
      />
    ),
    account: (
      <BankAccountSelector
        transactionPublicID={transactionPublicID}
        onError={() => setErroringStep("account")}
        onAddBankAccount={() => navigateToStep("bank")}
      />
    ),
  };

  if (activeStep === "detail") {
    return (
      <Stack gap={3} height="100%">
        <Stack pt={4} flexGrow={1}>
          {stepContentLookup.detail}
        </Stack>
        <SignInPrompt
          returnUrl={`${appUrl("customer-payment-pay")}/${encodeURIComponent(transactionPublicID)}`}
        />
      </Stack>
    );
  }

  return (
    <Stack gap={3} height={["100%", "auto"]}>
      <PrimaryPaper
        sx={{ height: activeStep === "account" ? "100%" : undefined }}
      >
        <Stack gap={8} pt={4} height="100%">
          <AnonymousPaymentStepper
            activeStep={activeStep}
            hasError={!!erroringStep}
          />
          {stepContentLookup[activeStep]}
        </Stack>
      </PrimaryPaper>
    </Stack>
  );
};
